import React, { useEffect, useRef, useState } from "react";
import logo from "../assets/logo.png";

const Section = ({ id, number, title, children, delay = 0 }) => {
    const [visible, setVisible] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section
            id={id}
            ref={ref}
            className={`scroll-mt-24 transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            <div className="flex items-center gap-4 mb-5">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 font-black text-sm shrink-0">
                    {number}
                </span>
                <h2 className="text-2xl font-bold text-white">{title}</h2>
            </div>
            <div className="pl-14">{children}</div>
        </section>
    );
};

const TocItem = ({ href, number, title, active }) => (
    <a
        href={href}
        className={`group flex items-center gap-3 py-2 text-sm transition-all duration-200 ${active ? "text-amber-400 font-semibold" : "text-slate-500 hover:text-slate-300"}`}
    >
        <span className={`w-6 h-6 flex items-center justify-center rounded-md text-[10px] font-bold transition-colors ${active ? "bg-amber-500 text-slate-950" : "bg-slate-800 text-slate-500 group-hover:bg-slate-700"}`}>
            {number}
        </span>
        <span className="line-clamp-1">{title}</span>
    </a>
);

export default function DeletePolicy() {
    const [activeSection, setActiveSection] = useState("intro");
    const [progress, setProgress] = useState(0);
    const contentRef = useRef(null);

    const sections = [
        { id: "intro", number: "1", title: "Introduction" },
        { id: "eligible", number: "2", title: "Data Eligible for Deletion" },
        { id: "process", number: "3", title: "Deletion Request Process" },
        { id: "verification", number: "4", title: "Verification & Security" },
        { id: "retention", number: "5", title: "Data Retention Requirements" },
        { id: "third-party", number: "6", title: "Third-Party Services" },
        { id: "contact", number: "7", title: "Contact Us" },
    ];

    useEffect(() => {
        const onScroll = () => {
            // Progress bar
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            setProgress(docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0);

            // Active section detection
            const sectionElements = sections.map((s) => ({
                id: s.id,
                element: document.getElementById(s.id),
            }));

            for (let i = sectionElements.length - 1; i >= 0; i--) {
                const { id, element } = sectionElements[i];
                if (element) {
                    const rect = element.getBoundingClientRect();
                    if (rect.top <= 150) {
                        setActiveSection(id);
                        break;
                    }
                }
            }
        };

        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <div className="bg-slate-950 min-h-screen text-slate-300 relative overflow-hidden">
            {/* Ambient background */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-amber-500/5 blur-[150px] rounded-full" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-600/5 blur-[120px] rounded-full" />
            </div>

            {/* Scroll Progress */}
            <div className="fixed top-0 left-0 w-full h-[3px] z-50 bg-transparent">
                <div
                    className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all duration-150 ease-out"
                    style={{ width: `${progress}%` }}
                />
            </div>

            <div className="max-w-7xl mx-auto px-6 py-24 relative z-10">
                <div className="grid lg:grid-cols-12 gap-12">

                    {/* Sidebar Navigation */}
                    <aside className="hidden lg:block lg:col-span-3">
                        <div className="sticky top-28 space-y-8">
                            <div>
                                <span className="text-xs font-bold uppercase tracking-widest text-amber-500 mb-4 block">
                                    Table of Contents
                                </span>
                                <nav className="space-y-1 border-l-2 border-slate-800 pl-4">
                                    {sections.map((s) => (
                                        <TocItem
                                            key={s.id}
                                            href={`#${s.id}`}
                                            number={s.number}
                                            title={s.title}
                                            active={activeSection === s.id}
                                        />
                                    ))}
                                </nav>
                            </div>

                            <div className="p-5 rounded-2xl bg-slate-900/80 border border-slate-800">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
                                        <i className="bi bi-shield-check" aria-hidden="true" />
                                    </div>
                                    <div>
                                        <p className="text-white font-bold text-sm">Need Help?</p>
                                        <p className="text-xs text-slate-500">We're here for you</p>
                                    </div>
                                </div>
                                <a
                                    href="mailto:privacy@delightgalaxy.com"
                                    className="block w-full text-center py-2.5 rounded-xl bg-amber-500 text-slate-950 font-bold text-sm hover:bg-amber-400 transition-colors"
                                >
                                    Email Support
                                </a>
                            </div>
                        </div>
                    </aside>

                    {/* Main Content */}
                    <main ref={contentRef} className="lg:col-span-9">
                        <div className="bg-slate-900/60 backdrop-blur-sm rounded-3xl border border-slate-800 shadow-2xl overflow-hidden">

                            {/* Header */}
                            <div className="relative p-8 md:p-12 pb-8 border-b border-slate-800 bg-gradient-to-br from-slate-900 to-slate-800/50">
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 text-xs font-semibold mb-6">
                                    <i className="bi bi-file-text" aria-hidden="true" />
                                    Legal Policy
                                </div>

                                <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white font-serif leading-tight">
                                    Data Deletion <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Policy</span>
                                </h1>

                                <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-slate-500">
                                    <span className="flex items-center gap-2">
                                        <i className="bi bi-calendar3" aria-hidden="true" />
                                        Last Updated: July 14, 2026
                                    </span>
                                    <span className="w-1 h-1 rounded-full bg-slate-700 hidden sm:block" />
                                    <span className="flex items-center gap-2">
                                        <i className="bi bi-building" aria-hidden="true" />
                                        Delight Galaxy Limited
                                    </span>
                                </div>
                            </div>

                            {/* Body */}
                            <div className="p-8 md:p-12 space-y-12 leading-relaxed">

                                <Section id="intro" number="1" title="Introduction">
                                    <p className="text-slate-400 leading-relaxed">
                                        Delight Galaxy Limited is committed to protecting the privacy and security
                                        of our users' information. This Data Deletion Policy explains how customers
                                        and users can request the removal of their personal information from our systems.
                                    </p>
                                </Section>

                                <Section id="eligible" number="2" title="Data Eligible for Deletion" delay={100}>
                                    <p className="text-slate-400 mb-5">
                                        Upon receiving and verifying a valid deletion request, we may remove the following information:
                                    </p>
                                    <ul className="grid sm:grid-cols-2 gap-3">
                                        {[
                                            "User account information and profiles",
                                            "Personal identification and contact information",
                                            "Uploaded files and user-generated content",
                                            "Application preferences and settings",
                                            "API tokens, access credentials, and connected account data",
                                            "Service usage information linked to the user account",
                                        ].map((item, i) => (
                                            <li key={i} className="flex items-start gap-3 p-3 rounded-xl bg-slate-950/50 border border-slate-800/50 text-sm text-slate-300">
                                                <i className="bi bi-check-circle-fill text-amber-500/80 mt-0.5 shrink-0" aria-hidden="true" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </Section>

                                <Section id="process" number="3" title="Deletion Request Process" delay={100}>
                                    <p className="text-slate-400 mb-6">
                                        Users or authorized account administrators may request data deletion by contacting our compliance team.
                                    </p>

                                    <div className="bg-slate-950 rounded-2xl p-6 md:p-8 border border-slate-800 relative overflow-hidden">
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 blur-3xl rounded-full" />

                                        <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                                            <i className="bi bi-send-fill text-amber-500" aria-hidden="true" />
                                            Data Removal Request
                                        </h3>

                                        <div className="grid sm:grid-cols-3 gap-6">
                                            <div className="space-y-2">
                                                <div className="w-10 h-10 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-amber-500">
                                                    <i className="bi bi-envelope" aria-hidden="true" />
                                                </div>
                                                <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Email</p>
                                                <a href="mailto:smuwanguzi2@gmail.com" className="text-sm text-white hover:text-amber-400 transition-colors font-medium">
                                                    smuwanguzi2@gmail.com
                                                </a>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="w-10 h-10 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-amber-500">
                                                    <i className="bi bi-tag-fill" aria-hidden="true" />
                                                </div>
                                                <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Subject Line</p>
                                                <p className="text-sm text-white font-medium">Data Deletion Request</p>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="w-10 h-10 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-amber-500">
                                                    <i className="bi bi-clock-history" aria-hidden="true" />
                                                </div>
                                                <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Processing Time</p>
                                                <p className="text-sm text-white font-medium">Within 30 days</p>
                                            </div>
                                        </div>
                                    </div>
                                </Section>

                                <Section id="verification" number="4" title="Verification & Security" delay={100}>
                                    <div className="p-5 rounded-xl bg-blue-500/5 border border-blue-500/10 flex items-start gap-4">
                                        <i className="bi bi-shield-lock text-blue-400 text-xl mt-1" aria-hidden="true" />
                                        <p className="text-slate-400 text-sm leading-relaxed">
                                            To protect user information, we may require verification before processing a deletion request.
                                            This ensures that only authorized individuals can request removal of account data.
                                        </p>
                                    </div>
                                </Section>

                                <Section id="retention" number="5" title="Data Retention Requirements" delay={100}>
                                    <p className="text-slate-400 leading-relaxed">
                                        Certain information may be retained where required by law, regulatory obligations,
                                        security requirements, fraud prevention measures, or legitimate business purposes.
                                        Retained information will remain protected and will only be used for the intended purpose.
                                    </p>
                                </Section>

                                <Section id="third-party" number="6" title="Third-Party Services" delay={100}>
                                    <p className="text-slate-400 leading-relaxed">
                                        Where our services integrate with third-party providers, deletion requests may also
                                        be processed according to the privacy policies and requirements of those providers.
                                    </p>
                                </Section>

                                <Section id="contact" number="7" title="Contact Us" delay={100}>
                                    <p className="text-slate-400 mb-6">
                                        If you have questions regarding this Data Deletion Policy or wish to request deletion
                                        of your information, contact us using the details below:
                                    </p>

                                    <div className="flex flex-col sm:flex-row items-start gap-6 p-6 rounded-2xl bg-slate-950 border border-slate-800">
                                        <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-900 border border-slate-800 shrink-0 overflow-hidden">
                                            <img
                                                src={logo}
                                                alt="Delight Galaxy"
                                                className="w-10 h-10 object-contain"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.innerHTML = '<span class="text-white font-black text-xl">D</span>';
                                                }}
                                            />
                                        </div>

                                        <div className="space-y-3 text-sm">
                                            <p className="text-white font-bold text-lg">Delight Galaxy Limited</p>

                                            <a href="mailto:smuwanguzi2@gmail.com" className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors">
                                                <i className="bi bi-envelope text-amber-500" aria-hidden="true" />
                                                smuwanguzi2@gmail.com
                                            </a>

                                            <a href="https://milestoneanddelight.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors">
                                                <i className="bi bi-globe text-amber-500" aria-hidden="true" />
                                                milestoneanddelight.com
                                            </a>

                                            <p className="flex items-center gap-2 text-slate-400">
                                                <i className="bi bi-geo-alt text-amber-500" aria-hidden="true" />
                                                Kampala, Uganda
                                            </p>
                                        </div>
                                    </div>
                                </Section>

                            </div>

                            {/* Footer of card */}
                            <div className="px-8 md:px-12 py-6 border-t border-slate-800 bg-slate-950/30 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-600">
                                <p>© {new Date().getFullYear()} Delight Galaxy Limited. All rights reserved.</p>
                                <div className="flex items-center gap-2">
                                    <i className="bi bi-shield-check text-emerald-500/80" aria-hidden="true" />
                                    <span>ISO 27001 Compliant Data Handling</span>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
}